import React from 'react'
import { X, Check } from 'react-feather'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useUserLocaleManager } from '../../state/user/hooks'
import Modal from '../Modal'
import { AutoColumn } from '../Column'
import { RowBetween } from '../Row'
import { Text } from 'rebass'

const LanguageModalContent = styled.div`
  width: 100%;
  padding: 0;
`

const Header = styled(RowBetween)`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.bg3};
`

const CloseIcon = styled(X)`
  cursor: pointer;
  color: ${({ theme }) => theme.text2};

  :hover {
    color: ${({ theme }) => theme.text1};
  }
`

const LanguageList = styled(AutoColumn)`
  padding: 0;
  max-height: 60vh;
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.bg1};
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.bg3};
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.bg4};
  }
`

const LanguageItem = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.125rem 1.5rem;
  cursor: pointer;
  background-color: ${({ theme, selected }) => (selected ? theme.bg2 : 'transparent')};
  transition: all 0.2s ease;
  border-bottom: 1px solid ${({ theme }) => theme.bg2};

  :hover {
    background-color: ${({ theme }) => theme.bg2};
  }

  :last-child {
    border-bottom: none;
  }
`

const LanguageName = styled(Text)<{ selected: boolean }>`
  font-size: 16px;
  font-weight: ${({ selected }) => (selected ? 600 : 400)};
  color: ${({ theme, selected }) => (selected ? theme.text1 : theme.text2)};
`

const CheckIcon = styled(Check)`
  color: ${({ theme }) => theme.primary1};
  width: 20px;
  height: 20px;
`

// 支持的语言列表
const SUPPORTED_LOCALES = [
  { code: 'en', name: 'English' },
  { code: 'zh-CN', name: '简体中文' },
  { code: 'zh-TW', name: '繁體中文' },
  { code: 'de', name: 'Deutsch' },
  { code: 'es-AR', name: 'Español (Argentina)' },
  { code: 'es-US', name: 'Español (US)' },
  { code: 'it-IT', name: 'Italiano' },
  { code: 'iw', name: 'עברית' },
  { code: 'ro', name: 'Română' },
  { code: 'ru', name: 'Русский' },
  { code: 'vi', name: 'Tiếng Việt' }
]

interface LanguageModalProps {
  isOpen: boolean
  onDismiss: () => void
}

export default function LanguageModal({ isOpen, onDismiss }: LanguageModalProps) {
  const { t } = useTranslation()
  const [currentLocale, setUserLocale] = useUserLocaleManager()

  const handleLanguageSelect = (code: string) => {
    setUserLocale(code)
    onDismiss()
  }

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} maxHeight={90}>
      <LanguageModalContent>
        <Header>
          <Text fontWeight={500} fontSize={18}>
            {t('language')}
          </Text>
          <CloseIcon onClick={onDismiss} />
        </Header>
        <LanguageList gap="0">
          {SUPPORTED_LOCALES.map(locale => (
            <LanguageItem
              key={locale.code}
              selected={currentLocale === locale.code}
              onClick={() => handleLanguageSelect(locale.code)}
            >
              <LanguageName selected={currentLocale === locale.code}>{locale.name}</LanguageName>
              {currentLocale === locale.code && <CheckIcon />}
            </LanguageItem>
          ))}
        </LanguageList>
      </LanguageModalContent>
    </Modal>
  )
}
